import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconArrowRight } from '@iwoca/icons';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';

import styles from './CompanyDetailsUK.module.css';
import { schema, variantSchema } from './CompanyDetailsUK.schema';
import CompanyInputUK from './CompanyInputUK/CompanyInputUK';
import TurnoverInputUK from './TurnoverInputUK';
import VatRegisteredRadiosUK from './VatRegisteredRadiosUK';
import { useIsInStep0EligibilityTest } from '../../../AnonymousSplitTest/useIsInStep0EligibilityTest';
import { usePutApplicationMutation } from '../../../api/lendingApi/lendingApiMutations';
import {
  useGetApplicationQuery,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../api/lendingApi/lendingApiQueries';
import { getUpdatedApplicationBody } from '../../../helpers/getApplicationBody';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useLocale } from '../../../hooks/useLocale';
import { useSignupRouterUK } from '../../../hooks/useSignupRouter/useSignupRouterUK';
import { useSplitTestContext } from '../../../hooks/useSplitTestContext';
import { TGetStateResponse } from '../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { EditAmount } from '../../EditAmount/EditAmount';
import {
  denormaliseCompanyDataUK,
  normaliseStepData,
} from '../../helpers/stateNormalisation';
import { BUSINESS_STEP_ID } from '../../helpers/steps';
import AmountInputUK from '../../LoanFields/AmountInputUK';
import LoanDurationUK from '../../LoanFields/LoanDurationUK';
import { getLoanFieldsFromApplicationUK } from '../../LoanFields/loanFields.helpers';
import { TLoanFieldsDataUK } from '../../LoanFields/loanOptions';
import LoanPurposeUK from '../../LoanFields/LoanPurposeUK';
import AccountCreatedModal from '../../SignupRoutes/AccountCreatedModal/AccountCreatedModal';

export type TCompanyDetailsDataUK = {
  companyName: string;
  companyNumber: string;
  companyType: GetCompanySearchResponse['data'][number]['company_type'];
  startDateMonth: string;
  startDateYear: string;
  last12MonthsTurnover: string;
  isVatRegistered: 'true' | 'false';
} & Partial<TLoanFieldsDataUK>;

type TProps = {
  handleStateUpdateError: (error: Error) => void;
};

const CompanyDetailsUK = ({ handleStateUpdateError }: TProps) => {
  const { state, isStateFetching } = useGetStateQuery<TGetStateResponse>();

  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[BUSINESS_STEP_ID],
  );

  const stateKey = useContextStateKey();
  const { hasAIQuestionSignupEnabled } = useSplitTestContext();

  const { navigateSignup } = useSignupRouterUK();

  const isInStep0EligibilityTest = useIsInStep0EligibilityTest();
  const { locale } = useLocale();
  const { application } = useGetApplicationQuery();
  const { mutatePutApplication } = usePutApplicationMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const resolver = (
    isInStep0EligibilityTest ? yupResolver(variantSchema) : yupResolver(schema)
  ) as Resolver<TCompanyDetailsDataUK>;

  const loanFieldsFromApplication = isInStep0EligibilityTest
    ? (getLoanFieldsFromApplicationUK(application) as TCompanyDetailsDataUK)
    : undefined;

  const methods = useForm<TCompanyDetailsDataUK>({
    resolver,
    defaultValues: loanFieldsFromApplication ?? { ...normalisedState },
    values: loanFieldsFromApplication,
    resetOptions: { keepDirtyValues: true },
  });

  const { handleSubmit, setValue } = methods;

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Business details',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  // Some partners forget to send us some company info. Easier to just wipe all company info
  // and get the customer to re-enter.
  useEffect(() => {
    if (isInStep0EligibilityTest) return;
    if (!normalisedState) return;

    if (
      !normalisedState.companyName ||
      !normalisedState.companyNumber ||
      !normalisedState.companyType ||
      !normalisedState.startDateMonth ||
      !normalisedState.startDateYear
    ) {
      setValue('companyName', '');
      setValue('companyNumber', '');
      // @ts-expect-error form state requires type to be an enum for submission
      setValue('companyType', '');
      setValue('startDateMonth', '');
      setValue('startDateYear', '');
    }
  }, [isInStep0EligibilityTest, normalisedState]);

  const onSubmit = async (values: TCompanyDetailsDataUK) => {
    if (!state || !stateKey) {
      return;
    }

    tracking.ampli.businessDetailsSubmitted(
      getAmplitudeDefaultEventProperties(),
    );

    try {
      setIsSubmitting(true);

      if (isInStep0EligibilityTest) {
        await mutatePutApplication({
          stateKey,
          body: getUpdatedApplicationBody(application, values, locale),
        });

        await navigateSignup({ route: 'check-eligibility' });

        return;
      }

      const denormalisedState = denormaliseCompanyDataUK(state, values);

      await navigateSignup({
        route: hasAIQuestionSignupEnabled
          ? 'company-questions'
          : 'check-eligibility',
        updatedState: denormalisedState.data,
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        handleStateUpdateError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <EditAmount currentStep="company_details" />
        <h1 className={styles.heading}>
          {isInStep0EligibilityTest
            ? 'Tell us what you need'
            : 'Introduce your business'}
        </h1>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <div className={styles.formFieldContainer}>
              {isInStep0EligibilityTest ? (
                <>
                  <div className={styles.formField}>
                    <AmountInputUK />
                  </div>
                  <div className={styles.formField}>
                    <LoanDurationUK
                      label="How long do you need the funds for?"
                      placeholder="Select an option"
                    />
                  </div>
                  <div className={styles.formField}>
                    <LoanPurposeUK placeholder="Select an option" />
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.formField}>
                    <CompanyInputUK />
                  </div>
                  <div className={styles.formField}>
                    <TurnoverInputUK />
                  </div>
                  <div className={styles.formField}>
                    <VatRegisteredRadiosUK />
                  </div>
                </>
              )}
            </div>
            <Button
              className={styles.submit}
              loading={isSubmitting || isStateFetching}
              type="submit"
            >
              {isInStep0EligibilityTest ? (
                'Continue'
              ) : (
                <>
                  Next step
                  <IconArrowRight />
                </>
              )}
            </Button>
          </form>
        </FormProvider>
      </div>
      {stateKey && <AccountCreatedModal stateKey={stateKey} />}
    </>
  );
};

export default CompanyDetailsUK;
